import React, { useState, useEffect } from 'react';
import { portfolio, market } from '../services/api';
import { Holding, Asset, Transaction } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { PdfReportGenerator } from './PdfReportGenerator';
import { HoldingsTable } from './HoldingsTable';
import { TransactionLedger } from './TransactionLedger';
import { TransactionUploadLog } from './TransactionUploadLog';
import { AssetDirectory } from './AssetDirectory';
import { DividendTracker } from './DividendTracker';
import { StockChartModal } from './StockChartModal';
import { OrderExecutionHub } from './OrderExecutionHub';
import { ordersApi, adminApi } from '../services/api';
import { Order } from '../types';
import { getAssetCategory, getSectorMeta } from '../utils/assetCategories';

interface Props {
  user: any;
  holdings: Holding[];
  simulatedHoldings: Holding[];
  assets: Asset[];
  stats: any;
  onSymbolSelect: (symbol: string) => void;
  onTrade?: (symbol: string, type: 'BUY' | 'SELL') => void;
  onRefresh: () => void;
  activeSubTab: 'overview' | 'buys' | 'prices' | 'calendar' | 'exports';
  onTabChange: (tab: 'overview' | 'buys' | 'prices' | 'calendar' | 'exports') => void;
  enabledModules: Record<string, boolean>;
  eurUsdRate?: number;
}

export const PortfolioOverviewHub: React.FC<Props> = ({
  user,
  holdings,
  simulatedHoldings,
  assets,
  stats,
  onSymbolSelect,
  onTrade,
  onRefresh,
  activeSubTab,
  onTabChange,
  enabledModules,
  eurUsdRate: propEurUsdRate
}) => {
  const { language, t } = useLanguage();
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [fxRate, setFxRate] = useState(propEurUsdRate || 1.09);

  useEffect(() => {
    if (propEurUsdRate) {
      setFxRate(propEurUsdRate);
    }
  }, [propEurUsdRate]);
  const [selectedInspectorSymbol, setSelectedInspectorSymbol] = useState<string | null>(null);
  const [chartModalHolding, setChartModalHolding] = useState<any | null>(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [cashBalances, setCashBalances] = useState<{ EUR: number; USD: number }>({ EUR: 0, USD: 0 });
  const [showCashModal, setShowCashModal] = useState<boolean>(false);
  const [cashCurrency, setCashCurrency] = useState<'EUR' | 'USD'>('EUR');
  const [cashAmount, setCashAmount] = useState<string>('');
  const [cashOpType, setCashOpType] = useState<'SET' | 'DEPOSIT' | 'WITHDRAW'>('SET');
  const [cashLoading, setCashLoading] = useState<boolean>(false);
  const [cashMsg, setCashMsg] = useState<string>('');
  const [activeSectorFilter, setActiveSectorFilter] = useState<string>('');

  // Target P/L Goal State & Modal
  const [portfolioGoal, setPortfolioGoal] = useState<{ targetType: 'percent' | 'fixed'; targetValue: number; annualHorizon: number }>({
    targetType: 'percent',
    targetValue: 15.0,
    annualHorizon: 1
  });
  const [showGoalModal, setShowGoalModal] = useState<boolean>(false);
  const [goalModalType, setGoalModalType] = useState<'percent' | 'fixed'>('percent');
  const [goalModalValue, setGoalModalValue] = useState<string>('15');
  const [goalModalLoading, setGoalModalLoading] = useState<boolean>(false);
  const [goalModalMsg, setGoalModalMsg] = useState<string>('');

  const fetchOrders = async () => {
    try {
      const res = await ordersApi.getOrders();
      setOrders(res.data?.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders in Hub', err);
    }
  };

  const fetchCashBalances = async () => {
    try {
      const res = await adminApi.getCashBalances();
      if (res.data?.balances) {
        setCashBalances(res.data.balances);
      }
    } catch (err) {
      console.error('Failed to fetch cash balances in Hub', err);
    }
  };

  const fetchPortfolioGoal = async () => {
    try {
      const res = await adminApi.getPortfolioGoal();
      if (res.data) {
        const type = res.data.targetType || 'percent';
        const val = typeof res.data.targetValue === 'number' ? res.data.targetValue : (parseFloat(res.data.targetValue) || 15.0);
        setPortfolioGoal({
          targetType: type,
          targetValue: val,
          annualHorizon: res.data.annualHorizon || 1
        });
        setGoalModalType(type);
        setGoalModalValue(String(val));
      }
    } catch (err) {
      console.warn('Failed to fetch portfolio goal, using default 15%', err);
    }
  };

  const handleUpdateCashModal = async (e: React.FormEvent) => {
    e.preventDefault();
    setCashLoading(true);
    setCashMsg('');
    try {
      await adminApi.updateCashBalance({
        currency: cashCurrency,
        amount: parseFloat(cashAmount) || 0,
        type: cashOpType
      });
      setCashMsg(language === 'sk' ? 'Hotovosť úspešne upravená!' : 'Cash balance updated successfully!');
      setCashAmount('');
      await fetchCashBalances();
      onRefresh();
      setTimeout(() => {
        setCashMsg('');
      }, 3000);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update cash balance');
    } finally {
      setCashLoading(false);
    }
  };

  const handleUpdateGoalModal = async (e: React.FormEvent) => {
    e.preventDefault();
    setGoalModalLoading(true);
    setGoalModalMsg('');
    try {
      const val = parseFloat(goalModalValue);
      if (isNaN(val) || val <= 0) return;
      await adminApi.updatePortfolioGoal({
        targetType: goalModalType,
        targetValue: val
      });
      setPortfolioGoal(prev => ({ ...prev, targetType: goalModalType, targetValue: val }));
      setGoalModalMsg(language === 'sk' ? 'Cieľ úspešne aktualizovaný!' : 'Target successfully updated!');
      setTimeout(() => {
        setShowGoalModal(false);
        setGoalModalMsg('');
      }, 700);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update target goal');
    } finally {
      setGoalModalLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const res = await portfolio.getTransactions();
      setTransactions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch transactions in Hub', err);
    }
  };

  const fetchFxRate = async () => {
    try {
      const res = await market.getFxRate();
      if (res.data && res.data.rate) {
        setFxRate(res.data.rate);
      }
    } catch (err) {
      console.error('Failed to fetch fx rate in Hub', err);
    }
  };

  useEffect(() => {
    fetchTransactions();
    fetchOrders();
    fetchCashBalances();
    fetchPortfolioGoal();
    if (!propEurUsdRate) {
      fetchFxRate();
    }
  }, [user?.id, user?.tenantId]);

  // Calculations for weights & FX conversion
  const activeHoldings = holdings.filter(h => Number(h.quantity || 0) > 0);

  const totalValue = activeHoldings.reduce((sum, h) => {
    const isEur = h.currency === 'EUR';
    const rawPrice = Number(h.current_price || h.avg_cost || 0);
    const priceInEur = isEur ? rawPrice : rawPrice / fxRate;
    return sum + (Number(h.quantity || 0) * priceInEur);
  }, 0);

  const totalInvested = activeHoldings.reduce((sum, h) => {
    const isEur = h.currency === 'EUR';
    const rawAvg = Number(h.avg_cost || 0);
    const avgInEur = isEur ? rawAvg : rawAvg / fxRate;
    return sum + (Number(h.quantity || 0) * avgInEur);
  }, 0);

  const holdingsWithWeights = activeHoldings.map(h => {
    const isEur = h.currency === 'EUR';
    const rawPrice = Number(h.current_price || h.avg_cost || 0);
    const priceInEur = isEur ? rawPrice : rawPrice / fxRate;
    const value = Number(h.quantity || 0) * priceInEur;
    return {
      ...h,
      value,
      weight: totalValue > 0 ? (value / totalValue) * 100 : 0
    };
  }).sort((a, b) => b.value - a.value);

  // Buys calculations
  const buyTransactions = transactions.filter(t => t.type === 'BUY');

  // Price changes calculations
  const priceMovements = assets.map(a => {
    const isUsd = a.currency === 'USD';
    const price = Number(a.current_price);
    const priceInEur = isUsd ? price / fxRate : price;
    return {
      ...a,
      priceInEur
    };
  });

  // Calendar setup (Dividend projections & Transaction milestones)
  const currentYear = new Date().getFullYear();
  const currentMonthIdx = new Date().getMonth(); // 0-11
  const monthNamesEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthNamesSk = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December'];
  const monthName = language === 'sk' ? monthNamesSk[currentMonthIdx] : monthNamesEn[currentMonthIdx];

  // Get days in current month
  const daysInMonth = new Date(currentYear, currentMonthIdx + 1, 0).getDate();
  const calendarCells = [];

  // Calculate day of the week offset for the 1st of the month (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = new Date(currentYear, currentMonthIdx, 1).getDay();
  // Adjust to Monday-start calendar (0 = Monday, 6 = Sunday)
  const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  // Add blank padding cells for the offset
  for (let i = 0; i < startOffset; i++) {
    calendarCells.push({
      day: null,
      txs: [],
      estDividend: 0
    });
  }
  
  // Calculate milestone event items for each active day
  for (let day = 1; day <= daysInMonth; day++) {
    const dayTransactions = transactions.filter(t => {
      if (!t.timestamp) return false;
      const d = new Date(t.timestamp);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonthIdx && d.getDate() === day;
    });
    
    // Simple dividend distribution mockup: if user has holdings, simulate dynamic monthly payouts on 10th and 25th
    const hasDividend = holdings.length > 0 && (day === 10 || day === 25);
    const estDividend = hasDividend 
      ? holdings.reduce((sum, h) => sum + (Number(h.quantity) * (Number(h.dividend_yield) || 0.02) * Number(h.current_price) / 12), 0)
      : 0;

    calendarCells.push({
      day,
      txs: dayTransactions,
      estDividend
    });
  }

  // Export to CSV helper
  const handleExportCSV = () => {
    const headers = 'Symbol,Name,Quantity,Avg Cost,Current Price,Value (EUR)\n';
    const rows = holdings.map(h => 
      `"${h.symbol}","${h.name}",${h.quantity},${h.avg_cost},${h.current_price},${(h.quantity * h.current_price).toFixed(2)}`
    ).join('\n');
    
    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `anogi_portfolio_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Total fees paid calculation
  const totalFees = transactions.reduce((sum, t) => {
    const fee = Number(t.fee || 0);
    const isEur = t.currency === 'EUR';
    return sum + (isEur ? fee : fee / fxRate);
  }, 0);

  // Target P/L Goal (dynamic % or fixed EUR)
  const targetPlGoal = portfolioGoal.targetType === 'fixed'
    ? portfolioGoal.targetValue
    : totalInvested * (portfolioGoal.targetValue / 100);
  const currentPl = totalValue - totalInvested;
  const targetProgressPct = targetPlGoal > 0 ? (currentPl / targetPlGoal) * 100 : 0;

  return (
    <div className="glass-panel p-6 space-y-6">
      {/* Dynamic Header Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 pb-4 border-b border-[var(--border-color)] font-mono">
        <div className="stat-card-premium flex flex-col justify-between overflow-hidden">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wider">
            {language === 'sk' ? 'Celkové Fondy' : 'Total Funds'}
          </span>
          <span className="text-xl sm:text-2xl font-black text-slate-900 truncate" title={`€${totalValue.toFixed(2)}`}>
            €{totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="stat-card-premium flex flex-col justify-between overflow-hidden">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wider">
            {t("statsInvested")}
          </span>
          <span className="text-base sm:text-lg font-extrabold text-slate-700 truncate" title={`€${totalInvested.toFixed(2)}`}>
            €{totalInvested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div 
          onClick={() => isAdmin && setShowCashModal(true)}
          className={`stat-card-premium flex flex-col justify-between overflow-hidden bg-emerald-50/50 border border-emerald-200/60 ${isAdmin ? 'cursor-pointer hover:border-emerald-400 hover:shadow-md hover:bg-emerald-50/80 transition-all group' : ''}`}
          title={isAdmin ? (language === 'sk' ? 'Kliknite pre zobrazenie a úpravu hotovosti' : 'Click to manage cash vault') : undefined}
        >
          <span className="text-[10px] uppercase font-bold text-emerald-800 block mb-1 tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-1">
              <span>{language === 'sk' ? 'Voľná Hotovosť' : 'Free Cash'}</span>
              {isAdmin && <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">✏️</span>}
            </span>
            <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.2 rounded font-mono group-hover:bg-emerald-200">EUR / USD</span>
          </span>
          <div className="flex flex-col">
            <span className="text-sm font-black text-emerald-950 flex items-center justify-between">
              <span>€{(cashBalances.EUR || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </span>
            <span className="text-[10px] font-bold text-emerald-700">
              ${(cashBalances.USD || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>
        <div className="stat-card-premium flex flex-col justify-between overflow-hidden">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wider">
            {t("totalFeesPaid")}
          </span>
          <span className="text-base sm:text-lg font-extrabold text-rose-600 truncate" title={`€${totalFees.toFixed(2)}`}>
            €{totalFees.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div className="stat-card-premium flex flex-col justify-between overflow-hidden">
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wider">
            {t("statsNetProfit")}
          </span>
          <span className={`text-base sm:text-lg font-black ${currentPl >= 0 ? 'text-emerald-600' : 'text-rose-600'} truncate`} title={`€${currentPl.toFixed(2)}`}>
            {currentPl >= 0 ? '+' : ''}€{currentPl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <div 
          onClick={() => isAdmin && setShowGoalModal(true)}
          className={`stat-card-premium flex flex-col justify-between overflow-hidden ${isAdmin ? 'cursor-pointer hover:border-indigo-400 hover:shadow-md transition-all group' : ''}`}
          title={isAdmin ? (language === 'sk' ? 'Kliknite pre nastavenie cieľového zhodnotenia' : 'Click to adjust target P/L benchmark') : undefined}
        >
          <span className="text-[10px] uppercase font-bold text-slate-500 block mb-1 tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-1">
              <span>{t("targetPlGoal")}</span>
              {isAdmin && <span className="text-[10px] opacity-0 group-hover:opacity-100 transition-opacity">✏️</span>}
            </span>
            <span className="text-[9px] bg-indigo-50 text-indigo-700 px-1 py-0.2 rounded font-mono border border-indigo-100">
              {portfolioGoal.targetType === 'percent' ? `+${portfolioGoal.targetValue}%` : 'FIX'}
            </span>
          </span>
          <div className="flex items-center justify-between gap-1 flex-wrap">
            <span className="text-base sm:text-lg font-black text-indigo-600 truncate" title={`€${targetPlGoal.toFixed(2)}`}>
              €{targetPlGoal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
            <span className={`text-[10px] font-extrabold px-1.5 py-0.5 rounded border ${
              targetProgressPct >= 100
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : targetProgressPct >= 0
                ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                : 'bg-rose-50 text-rose-700 border-rose-200'
            }`}>
              {targetProgressPct.toFixed(0)}%
            </span>
          </div>
        </div>
      </div>

      {/* Stock Detail Modal for Inspector "Open Chart" */}
      {chartModalHolding && (
        <StockChartModal
          symbol={chartModalHolding.symbol}
          assetId={chartModalHolding.asset_id}
          name={chartModalHolding.name}
          currentPrice={chartModalHolding.current_price}
          avgCost={chartModalHolding.avg_cost}
          quantity={chartModalHolding.quantity}
          currency={chartModalHolding.currency}
          onClose={() => setChartModalHolding(null)}
        />
      )}

      {/* REVOLUTIONIZED: Sektorové Rozloženie & Interaktívny Inšpektor Akcie */}
      {(() => {
        // 1. Group active holdings into real economic sectors
        const sectorGroups: Record<string, { meta: any; value: number; holdings: typeof holdingsWithWeights }> = {};
        
        holdingsWithWeights.forEach(hw => {
          const cat = getAssetCategory(hw.symbol, hw.name || '', hw.sector, language);
          const meta = getSectorMeta(cat);
          if (!sectorGroups[meta.key]) {
            sectorGroups[meta.key] = {
              meta,
              value: 0,
              holdings: []
            };
          }
          sectorGroups[meta.key].value += hw.value;
          sectorGroups[meta.key].holdings.push(hw);
        });

        const sortedSectors = Object.values(sectorGroups).sort((a, b) => b.value - a.value);

        // Find currently focused holding for inspector
        const activeInspectorHolding = holdingsWithWeights.find(h => h.symbol === selectedInspectorSymbol) || null;

        // Calculations for active inspector holding
        const isEur = activeInspectorHolding?.currency === 'EUR';
        const rawCurPrice = Number(activeInspectorHolding?.current_price || activeInspectorHolding?.avg_cost || 0);
        const curPriceInEur = isEur ? rawCurPrice : rawCurPrice / fxRate;
        const rawAvgCost = Number(activeInspectorHolding?.avg_cost || 0);
        const avgCostInEur = isEur ? rawAvgCost : rawAvgCost / fxRate;
        const holdingVal = Number(activeInspectorHolding?.quantity || 0) * curPriceInEur;
        const holdingCost = Number(activeInspectorHolding?.quantity || 0) * avgCostInEur;
        const holdingPl = holdingVal - holdingCost;
        const holdingPlPct = holdingCost > 0 ? (holdingPl / holdingCost) * 100 : 0;
        const weightPct = totalValue > 0 ? (holdingVal / totalValue) * 100 : 0;

        return (
          <div className="glass-panel p-4 sm:p-6 space-y-5 bg-white/80 border border-slate-200 shadow-sm rounded-3xl">
            {/* Header with Title and Mode Badges */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
                  <span>📊</span>
                  {language === 'sk' ? 'Sektorové Rozloženie & Inšpektor Aktív' : 'Sector Breakdown & Asset Inspector'}
                </h3>
                <p className="text-[10px] text-stone-500 font-mono mt-0.5">
                  {language === 'sk' 
                    ? 'Reálne sektorové váhy, menová expozícia (EUR/USD) a detail vybraného aktíva po kliknutí' 
                    : 'Real economic sector weights, currency exposure (EUR/USD) and interactive asset inspector'}
                </p>
              </div>

              {/* Concentration Risk Alert Pill */}
              {holdingsWithWeights.length > 0 && holdingsWithWeights[0].weight > 40 && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-[10px] font-black shrink-0">
                  <span className="animate-pulse">⚠️</span>
                  <span>
                    {language === 'sk'
                      ? `Koncentračné riziko: ${holdingsWithWeights[0].symbol} tvorí ${holdingsWithWeights[0].weight.toFixed(0)}% portfólia`
                      : `Concentration alert: ${holdingsWithWeights[0].symbol} makes up ${holdingsWithWeights[0].weight.toFixed(0)}%`}
                  </span>
                </div>
              )}
            </div>

            {/* 1. SEKTOROVÁ VIZUÁLNA LIŠTA (Proporcionálny farebný pás s garantovanou čitateľnosťou) */}
            <div className="space-y-2">
              <div className="w-full h-9 rounded-xl flex overflow-hidden border border-slate-300 shadow-inner bg-slate-200/80 p-0.5">
                {sortedSectors.map((sec) => {
                  const secWeight = totalValue > 0 ? (sec.value / totalValue) * 100 : 0;
                  if (secWeight <= 0) return null;
                  const secName = language === 'sk' ? sec.meta.nameSk : sec.meta.nameEn;
                  const isSecActive = activeSectorFilter === sec.meta.key || 
                    activeSectorFilter === sec.meta.nameSk || 
                    activeSectorFilter === sec.meta.nameEn ||
                    (activeSectorFilter && sec.meta.nameSk.toLowerCase().includes(activeSectorFilter.toLowerCase())) ||
                    (activeSectorFilter && activeSectorFilter.toLowerCase().includes(sec.meta.key.toLowerCase()));

                  return (
                    <button
                      key={sec.meta.key}
                      onClick={() => {
                        setActiveSectorFilter(prev => {
                          const wasActive = prev === sec.meta.key || prev === sec.meta.nameSk || prev === sec.meta.nameEn || (prev && sec.meta.nameSk.toLowerCase().includes(prev.toLowerCase()));
                          return wasActive ? '' : sec.meta.nameSk;
                        });
                      }}
                      style={{ width: `${Math.max(secWeight, 4)}%`, backgroundColor: sec.meta.color }}
                      className={`h-full relative group transition-all duration-300 flex items-center justify-center text-[11px] font-black text-white px-1 overflow-hidden cursor-pointer ${
                        isSecActive ? 'ring-2 ring-white ring-inset brightness-125 shadow-md' : 'hover:brightness-110'
                      }`}
                      title={`${secName}: ${secWeight.toFixed(1)}% (€${sec.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}) - ${language === 'sk' ? 'Kliknite pre filter' : 'Click to filter'}`}
                    >
                      <span className="truncate flex items-center justify-center gap-1 drop-shadow font-mono text-[10px]">
                        <span className="text-xs shrink-0">{sec.meta.icon}</span>
                        {secWeight >= 5 && (
                          <span className="font-extrabold">{secWeight.toFixed(0)}%</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Sektorové Legendy a Percentá (Kompaktné interaktívne karty) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 font-mono">
                {sortedSectors.map((sec) => {
                  const secWeight = totalValue > 0 ? (sec.value / totalValue) * 100 : 0;
                  const symbolsList = sec.holdings.map(h => h.symbol).join(', ');
                  const secName = language === 'sk' ? sec.meta.nameSk : sec.meta.nameEn;
                  const isSecActive = activeSectorFilter === sec.meta.key || 
                    activeSectorFilter === sec.meta.nameSk || 
                    activeSectorFilter === sec.meta.nameEn ||
                    (activeSectorFilter && sec.meta.nameSk.toLowerCase().includes(activeSectorFilter.toLowerCase())) ||
                    (activeSectorFilter && activeSectorFilter.toLowerCase().includes(sec.meta.key.toLowerCase()));

                  return (
                    <button 
                      key={sec.meta.key}
                      onClick={() => {
                        setActiveSectorFilter(prev => {
                          const wasActive = prev === sec.meta.key || prev === sec.meta.nameSk || prev === sec.meta.nameEn || (prev && sec.meta.nameSk.toLowerCase().includes(prev.toLowerCase()));
                          return wasActive ? '' : sec.meta.nameSk;
                        });
                      }}
                      className={`p-2.5 rounded-2xl border text-left flex flex-col justify-between transition-all cursor-pointer group ${
                        isSecActive 
                          ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-indigo-500' 
                          : 'bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 shadow-2xs'
                      }`}
                      style={{ borderLeftWidth: '4px', borderLeftColor: sec.meta.color }}
                      title={language === 'sk' ? `Kliknite pre filtrovanie sektora: ${secName}` : `Click to filter sector: ${secName}`}
                    >
                      <div className="flex items-center justify-between gap-1 w-full">
                        <span className={`text-[11px] font-extrabold flex items-center gap-1.5 truncate ${
                          isSecActive ? 'text-white' : 'text-slate-900'
                        }`}>
                          <span className="text-xs">{sec.meta.icon}</span>
                          <span className="truncate">{secName}</span>
                        </span>
                        <span className={`text-[11px] font-black px-1.5 py-0.5 rounded border ${
                          isSecActive 
                            ? 'bg-white/20 text-white border-white/20' 
                            : 'bg-slate-100 text-slate-900 border-slate-200'
                        }`}>
                          {secWeight.toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-baseline justify-between text-[10px] w-full">
                        <span className={`font-bold ${isSecActive ? 'text-emerald-400 font-black' : 'text-slate-800'}`}>
                          €{sec.value.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                        <span className={`text-[9px] truncate max-w-[90px] ${isSecActive ? 'text-slate-300' : 'text-slate-400'}`} title={symbolsList}>
                          {symbolsList}
                        </span>
                      </div>
                      {isSecActive && (
                        <div className="mt-1 pt-1 border-t border-white/20 flex items-center justify-between text-[9px] text-indigo-300 font-bold">
                          <span>✓ {language === 'sk' ? 'Aktívny filter' : 'Active filter'}</span>
                          <span className="text-[8px] opacity-75">✕ {language === 'sk' ? 'zrušiť' : 'clear'}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 2. DLAŽDICE AKCIÍ S INTERAKTÍVNYM FOCUSOM A SEKTOROVÝM FAREBNÝM ROZLÍŠENÍM */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
                <span>{language === 'sk' ? 'Jednotlivé akcie v portfóliu (kliknite pre detail):' : 'Portfolio assets (click to inspect):'}</span>
                <span className="text-[10px] font-mono">{activeHoldings.length} {language === 'sk' ? 'titulov' : 'positions'}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
                {holdingsWithWeights.map((hw: any) => {
                  const isSelected = selectedInspectorSymbol === hw.symbol;
                  const cat = getAssetCategory(hw.symbol, hw.name || '', hw.sector, language);
                  const meta = getSectorMeta(cat);

                  return (
                    <button
                      key={hw.symbol}
                      onClick={() => {
                        setSelectedInspectorSymbol(isSelected ? null : hw.symbol);
                        if (onSymbolSelect) onSymbolSelect(hw.symbol);
                      }}
                      style={{
                        borderLeftWidth: '4px',
                        borderLeftColor: meta.color,
                        borderTopColor: isSelected ? meta.color : undefined
                      }}
                      className={`text-left p-3 rounded-2xl border transition-all flex flex-col justify-between h-[96px] relative overflow-hidden group shadow-2xs ${
                        isSelected 
                          ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-indigo-500' 
                          : 'bg-white hover:bg-slate-50/90 text-[#1c1917] border-slate-200 hover:border-slate-400 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex justify-between items-start w-full">
                        <div className="flex items-center gap-1.5">
                          <span className="font-black text-xs">{hw.symbol}</span>
                          <span className="text-xs" title={language === 'sk' ? meta.nameSk : meta.nameEn}>
                            {meta.icon}
                          </span>
                        </div>
                        <span 
                          style={!isSelected ? { backgroundColor: `${meta.color}15`, color: meta.color } : {}}
                          className={`text-[10px] font-mono font-extrabold px-1.5 py-0.5 rounded border border-transparent ${
                            isSelected ? 'bg-slate-800 text-emerald-400 border-slate-700' : ''
                          }`}
                        >
                          {hw.weight ? hw.weight.toFixed(1) : '100.0'}%
                        </span>
                      </div>

                      <div className="mt-1">
                        <div className="text-xs font-black">
                          €{Number(hw.value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </div>
                        <div className={`text-[9px] font-mono mt-0.5 truncate ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                          {Number(hw.quantity).toLocaleString()} {language === 'sk' ? 'ks' : 'units'} @ {hw.currency === 'USD' ? '$' : '€'}{Number(hw.current_price || 0).toFixed(2)}
                        </div>
                      </div>

                      {/* Active Indicator Chevron */}
                      {isSelected && (
                        <div className="absolute bottom-1 right-2 text-indigo-400 text-xs font-black animate-bounce">
                          ▲
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 3. INTERAKTÍVNY INŠPEKTOR AKCIE (Vysúvací detail po kliknutí) */}
            {activeInspectorHolding && (
              <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white p-4 sm:p-5 rounded-3xl border border-indigo-500/30 shadow-lg space-y-4 animate-[fadeIn_0.2s_ease-out] font-mono">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-3 border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-lg font-black text-indigo-300">
                      {activeInspectorHolding.symbol.slice(0, 2)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-base font-black text-white">{activeInspectorHolding.symbol}</h4>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/10 text-slate-300 font-bold">
                          {activeInspectorHolding.currency || 'EUR'}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                          {getAssetCategory(activeInspectorHolding.symbol, activeInspectorHolding.name || '', activeInspectorHolding.sector, language)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 font-sans mt-0.5">{activeInspectorHolding.name}</p>
                    </div>
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <button
                      onClick={() => setChartModalHolding(activeInspectorHolding)}
                      className="px-3.5 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs transition-all shadow flex items-center gap-1.5"
                    >
                      <span>📈</span>
                      <span>{language === 'sk' ? 'Otvoriť Graf & Históriu' : 'Open Chart & History'}</span>
                    </button>
                    <button
                      onClick={() => onTabChange('buys')}
                      className="px-3.5 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-extrabold text-xs transition-all border border-white/20 flex items-center gap-1.5"
                    >
                      <span>⚡</span>
                      <span>{language === 'sk' ? 'Kúpiť / Predať' : 'Trade Asset'}</span>
                    </button>
                    <button
                      onClick={() => setSelectedInspectorSymbol(null)}
                      className="w-7 h-7 rounded-xl bg-white/10 hover:bg-rose-500/30 text-slate-300 hover:text-white font-bold text-xs flex items-center justify-center transition-colors ml-1"
                      title={language === 'sk' ? 'Zavrieť inšpektor' : 'Close inspector'}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* 4-Box Metric Breakdown */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  {/* Trhová hodnota */}
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 block mb-1">
                      {language === 'sk' ? 'Trhová Hodnota' : 'Market Value'}
                    </span>
                    <div className="flex flex-wrap items-baseline gap-1">
                      <span className="text-sm sm:text-base font-black text-white">
                        €{holdingVal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      {!isEur && (
                        <span className="text-[10px] text-slate-400 font-normal">
                          (${((Number(activeInspectorHolding.quantity) * rawCurPrice)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-indigo-300 block mt-0.5">
                      {weightPct.toFixed(1)}% {language === 'sk' ? 'z portfólia' : 'of portfolio'}
                      {!isEur && ` • $${rawCurPrice.toFixed(2)}/ks`}
                    </span>
                  </div>

                  {/* Nákupný základ */}
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 block mb-1">
                      {language === 'sk' ? 'Priemerná Nákupná Cena' : 'Avg Cost Basis'}
                    </span>
                    <div className="flex flex-wrap items-baseline gap-1">
                      <span className="text-sm sm:text-base font-black text-slate-200">
                        €{avgCostInEur.toFixed(2)}
                      </span>
                      {!isEur && (
                        <span className="text-[10px] text-slate-400 font-normal">
                          (${rawAvgCost.toFixed(2)})
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-400 block mt-0.5">
                      {Number(activeInspectorHolding.quantity).toLocaleString()} {language === 'sk' ? 'ks' : 'units'}
                      {!isEur && ` • FX €/$: ${fxRate.toFixed(4)}`}
                    </span>
                  </div>

                  {/* Čistý zisk / strata */}
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 block mb-1">
                      {language === 'sk' ? 'Čistý Zisk / Strata' : 'Unrealized P/L'}
                    </span>
                    <span className={`text-sm sm:text-base font-black ${holdingPl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {holdingPl >= 0 ? '+' : ''}€{holdingPl.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className={`text-[10px] font-extrabold block mt-0.5 ${holdingPl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {holdingPl >= 0 ? '+' : ''}{holdingPlPct.toFixed(2)}%
                    </span>
                  </div>

                  {/* Strategické AI Odporúčanie pre váhu */}
                  <div className="bg-white/5 p-3 rounded-2xl border border-white/10 flex flex-col justify-between">
                    <div>
                      <span className="text-[9px] uppercase tracking-wider text-slate-400 block mb-1">
                        {language === 'sk' ? 'Status Diverzifikácie' : 'Allocation Status'}
                      </span>
                      <span className={`text-xs font-black inline-block px-2 py-0.5 rounded ${
                        weightPct > 35 
                          ? 'bg-amber-400/20 text-amber-300 border border-amber-400/30' 
                          : weightPct < 3 
                          ? 'bg-sky-400/20 text-sky-300 border border-sky-400/30' 
                          : 'bg-emerald-400/20 text-emerald-300 border border-emerald-400/30'
                      }`}>
                        {weightPct > 35 
                          ? (language === 'sk' ? '⚠️ VYSOKÁ KONCENTRÁCIA' : '⚠️ OVERWEIGHT') 
                          : weightPct < 3 
                          ? (language === 'sk' ? '🌱 MINORITNÁ POZÍCIA' : '🌱 UNDERWEIGHT') 
                          : (language === 'sk' ? '✓ VYVÁŽENÁ VÁHA' : '✓ BALANCED')}
                      </span>
                    </div>
                    <span className="text-[9px] text-slate-300 mt-1">
                      {weightPct > 35
                        ? (language === 'sk' ? 'Zvážte realizáciu ziskov' : 'Consider rebalancing')
                        : (language === 'sk' ? 'Priestor na akumuláciu' : 'Room to accumulate')}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Guided Hub Selection Tabs - Sticky with backdrop blur */}
      <div className="sticky top-0 z-20 py-2.5 -mx-6 px-6 bg-[var(--bg-primary)]/85 backdrop-blur-md border-b border-[var(--border-color)]/60 transition-all">
        <div className="flex flex-wrap gap-2 p-1 bg-[var(--accent-color)] border border-[var(--border-color)] rounded-2xl w-fit shadow-xs">
          <button
            onClick={() => onTabChange('overview')}
            className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${
              activeSubTab === 'overview' 
                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            💼 {t("subtabOverview")}
          </button>
          <button
            onClick={() => onTabChange('buys')}
            className={`px-4 py-2 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 ${
              activeSubTab === 'buys' 
                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            <span>📜</span>
            <span>{t("subtabBuys")}</span>
          </button>
          <button
            onClick={() => onTabChange('prices')}
            className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${
              activeSubTab === 'prices' 
                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            📊 {t("subtabPrices")}
          </button>
          <button
            onClick={() => onTabChange('calendar')}
            className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${
              activeSubTab === 'calendar' 
                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            📅 {t("subtabCalendar")}
          </button>
          <button
            onClick={() => onTabChange('exports')}
            className={`px-4 py-2 text-xs font-bold rounded-xl transition-all ${
              activeSubTab === 'exports' 
                ? 'bg-[var(--text-primary)] text-[var(--bg-primary)] shadow-sm' 
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
            }`}
          >
            📂 {t("subtabExports")}
          </button>
        </div>
      </div>

      {/* Tab Panels */}
      {activeSubTab === 'overview' && (
        <div className="space-y-6 animate-[fadeIn_0.2s_ease-out]">
          <HoldingsTable 
            holdings={holdings} 
            onSelectSymbol={onSymbolSelect} 
            selectedSector={activeSectorFilter}
            onSectorChange={setActiveSectorFilter}
            fxRate={fxRate}
          />
          <TransactionUploadLog onRefresh={onRefresh} />
        </div>
      )}

      {activeSubTab === 'buys' && (
        <div className="space-y-6 animate-[fadeIn_0.2s_ease-out]">
          <TransactionUploadLog onRefresh={onRefresh} />
        </div>
      )}

      {activeSubTab === 'prices' && (
        <div className="space-y-6 animate-[fadeIn_0.2s_ease-out]">
          <AssetDirectory assets={assets} onSelectSymbol={(sym) => onSymbolSelect(sym)} onRefreshAssets={onRefresh} />
          {enabledModules['dividends'] && (
            <DividendTracker holdings={simulatedHoldings} />
          )}
        </div>
      )}

      {activeSubTab === 'calendar' && (
        <div className="space-y-4 animate-[fadeIn_0.2s_ease-out]">
          <div className="flex justify-between items-center pb-2 border-b border-[var(--border-color)]">
            <h4 className="font-extrabold text-sm text-[var(--text-primary)]">{monthName} {currentYear}</h4>
            <span className="text-[10px] text-stone-500 font-mono">
              🟢 {language === 'sk' ? 'Transakcie' : 'Trades'} • 🪙 {language === 'sk' ? 'Odhad. Dividendy' : 'Est. Dividends'}
            </span>
          </div>

          <div className="grid grid-cols-7 gap-2 text-center text-[10px] text-stone-400 font-bold uppercase tracking-wider">
            <div>{language === 'sk' ? 'Po' : 'Mo'}</div>
            <div>{language === 'sk' ? 'Ut' : 'Tu'}</div>
            <div>{language === 'sk' ? 'St' : 'We'}</div>
            <div>{language === 'sk' ? 'Št' : 'Th'}</div>
            <div>{language === 'sk' ? 'Pi' : 'Fr'}</div>
            <div>{language === 'sk' ? 'So' : 'Sa'}</div>
            <div>{language === 'sk' ? 'Ne' : 'Su'}</div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {calendarCells.map((c, index) => {
              if (c.day === null) {
                return <div key={`empty-${index}`} className="p-2 border border-transparent min-h-[60px]" />;
              }
              return (
                <div 
                  key={`day-${c.day}`} 
                  className={`p-2 border border-[var(--border-color)]/20 rounded-xl min-h-[60px] flex flex-col justify-between hover:bg-stone-50/50 transition-all ${
                    c.txs.length > 0 ? 'bg-emerald-50/40 border-emerald-300' : 'bg-[var(--bg-secondary)]'
                  }`}
                >
                  <span className="font-extrabold text-stone-400 text-[10px]">{c.day}</span>
                  <div className="space-y-1">
                    {c.txs.map(t => (
                      <div 
                        key={t.id} 
                        className={`text-[8px] font-black rounded px-1 text-center truncate ${
                          t.type === 'BUY' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}
                        title={`${t.type} ${t.quantity} ${t.symbol}`}
                      >
                        {t.symbol}
                      </div>
                    ))}
                    {c.estDividend > 0 && (
                      <div 
                        className="text-[8px] font-bold bg-amber-100 text-amber-800 rounded px-1 text-center"
                        title={`Estimated Dividend payout: €${c.estDividend.toFixed(2)}`}
                      >
                        🪙 €{c.estDividend.toFixed(0)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {activeSubTab === 'exports' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-[fadeIn_0.2s_ease-out]">
          <PdfReportGenerator holdings={holdings} stats={stats} transactions={transactions} />
          
          <div className="bg-white border border-[var(--border-color)] rounded-3xl p-6 shadow-sm shadow-slate-100 font-mono text-xs space-y-4">
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917] flex items-center gap-2">
                <span>📊</span>
                {t("csvExportTitle")}
              </h3>
              <p className="text-[10px] text-stone-500 mt-0.5">
                {t("csvExportDesc")}
              </p>
            </div>
            
            <div className="bg-stone-50 border border-stone-200/55 rounded-2xl p-5 space-y-2 text-[10px] text-stone-600">
              <div>• {language === 'sk' ? 'Formát exportu:' : 'Export Format:'} <strong>CSV / RFC4180</strong></div>
              <div>• {language === 'sk' ? 'Kompatibilita:' : 'Compatibility:'} <strong>Excel, Google Sheets</strong></div>
            </div>

            <button
              onClick={handleExportCSV}
              className="w-full bg-[var(--text-primary)] hover:opacity-90 text-[var(--bg-primary)] font-extrabold py-2.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
            >
              <span>💾</span>
              {t("exportToCsvBtn")}
            </button>
          </div>
        </div>
      )}

      {/* Quick Cash Vault Management Modal */}
      {showCashModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-[fadeIn_0.15s_ease-out]">
          <div className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-3xl p-6 sm:p-7 max-w-lg w-full shadow-2xl relative">
            <button
              onClick={() => { setShowCashModal(false); setCashMsg(''); }}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg w-8 h-8 rounded-full flex items-center justify-center bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 transition-colors"
            >
              ✕
            </button>

            <div className="flex items-center gap-3 mb-5">
              <span className="text-2xl p-2 bg-emerald-100 dark:bg-emerald-950/60 rounded-2xl">🏦</span>
              <div>
                <h3 className="text-lg font-black text-[var(--text-primary)]">
                  {language === 'sk' ? 'Správa Voľnej Hotovosti (Cash Vault)' : 'Free Cash Vault Management'}
                </h3>
                <p className="text-xs text-[var(--text-secondary)]">
                  {language === 'sk' ? 'Aktuálne zostatky a zaúčtovanie vkladov / výberov' : 'Current liquidity balances and deposits / withdrawals'}
                </p>
              </div>
            </div>

            {cashMsg && (
              <div className="p-3 mb-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 rounded-xl font-bold text-xs flex items-center gap-2">
                <span>✓</span>
                <span>{cashMsg}</span>
              </div>
            )}

            {/* Current Balances Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="p-4 bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60 rounded-2xl">
                <span className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400 block tracking-wider">
                  EUR (€)
                </span>
                <span className="text-xl font-black text-emerald-950 dark:text-emerald-200 mt-0.5 block">
                  €{Number(cashBalances.EUR || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="p-4 bg-sky-50/70 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-800/60 rounded-2xl">
                <span className="text-[10px] uppercase font-bold text-sky-700 dark:text-sky-400 block tracking-wider">
                  USD ($)
                </span>
                <span className="text-xl font-black text-sky-950 dark:text-sky-200 mt-0.5 block">
                  ${Number(cashBalances.USD || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Adjustment Form */}
            <form onSubmit={handleUpdateCashModal} className="bg-[var(--accent-color)] p-4 sm:p-5 rounded-2xl border border-[var(--border-color)] space-y-4">
              <span className="block text-[11px] font-extrabold uppercase tracking-wider text-[var(--text-secondary)]">
                {language === 'sk' ? 'Úprava / Pohyb Hotovosti' : 'Adjust / Cash Movement'}
              </span>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[9px] uppercase font-bold text-[var(--text-secondary)] mb-1">
                    {language === 'sk' ? 'Mena' : 'Currency'}
                  </label>
                  <select
                    value={cashCurrency}
                    onChange={e => setCashCurrency(e.target.value as any)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl font-bold text-xs"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[9px] uppercase font-bold text-[var(--text-secondary)] mb-1">
                    {language === 'sk' ? 'Typ operácie' : 'Operation'}
                  </label>
                  <select
                    value={cashOpType}
                    onChange={e => setCashOpType(e.target.value as any)}
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl font-bold text-xs"
                  >
                    <option value="SET">{language === 'sk' ? 'Nastaviť zostatok' : 'Set Exact Balance'}</option>
                    <option value="DEPOSIT">{language === 'sk' ? 'Vklad (+)' : 'Deposit (+)'}</option>
                    <option value="WITHDRAW">{language === 'sk' ? 'Výber (-)' : 'Withdrawal (-)'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[9px] uppercase font-bold text-[var(--text-secondary)] mb-1">
                    {language === 'sk' ? 'Suma' : 'Amount'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={cashAmount}
                    onChange={e => setCashAmount(e.target.value)}
                    required
                    className="w-full px-3 py-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl font-bold text-xs"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setShowCashModal(false)}
                  className="px-4 py-2 border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-bold text-xs rounded-xl transition-all"
                >
                  {language === 'sk' ? 'Zavrieť' : 'Close'}
                </button>
                <button
                  type="submit"
                  disabled={cashLoading}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl shadow-sm transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  {cashLoading ? '...' : (language === 'sk' ? 'Uložiť zmenu' : 'Save Balance')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal na rýchle nastavenie Target P/L Goal */}
      {showGoalModal && (
        <div 
          onClick={() => setShowGoalModal(false)}
          className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 animate-[fadeIn_0.15s_ease-out]"
        >
          <div 
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-6 w-full max-w-md font-mono space-y-4 text-xs"
          >
            <div className="flex justify-between items-start border-b border-slate-200/80 pb-3">
              <div>
                <h4 className="font-extrabold text-sm text-slate-900 flex items-center gap-1.5 uppercase">
                  <span>🎯</span>
                  <span>{t("targetPlModalTitle")}</span>
                </h4>
                <p className="text-[10px] text-slate-500 mt-0.5">
                  {t("targetPlModalDesc")}
                </p>
              </div>
              <button 
                onClick={() => setShowGoalModal(false)}
                className="text-slate-400 hover:text-slate-700 font-bold text-sm px-1.5 py-0.5"
              >
                ✕
              </button>
            </div>

            {goalModalMsg && (
              <div className="p-2.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-bold text-[11px]">
                ✓ {goalModalMsg}
              </div>
            )}

            <form onSubmit={handleUpdateGoalModal} className="space-y-4">
              <div>
                <label className="block text-[9px] uppercase font-bold text-slate-500 mb-1">
                  {language === 'sk' ? 'Formát Cieľa' : 'Target Format'}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setGoalModalType('percent');
                      if (parseFloat(goalModalValue) > 100) setGoalModalValue('15');
                    }}
                    className={`py-2 px-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                      goalModalType === 'percent'
                        ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <span>📈</span>
                    <span>{t("targetTypePercent")}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGoalModalType('fixed');
                      if (parseFloat(goalModalValue) <= 100) setGoalModalValue('30000');
                    }}
                    className={`py-2 px-3 rounded-xl border font-bold text-xs flex items-center justify-center gap-1.5 transition-all ${
                      goalModalType === 'fixed'
                        ? 'bg-slate-900 text-white border-slate-900 shadow-2xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    <span>💶</span>
                    <span>{t("targetTypeFixed")}</span>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[9px] uppercase font-bold text-slate-500 mb-1">
                  {goalModalType === 'percent' 
                    ? (language === 'sk' ? 'Cieľové zhodnotenie (% p.a.)' : 'Target Return (% p.a.)')
                    : (language === 'sk' ? 'Cieľový čistý zisk v EUR' : 'Target Net Gain in EUR')}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="any"
                    min="0.1"
                    required
                    value={goalModalValue}
                    onChange={e => setGoalModalValue(e.target.value)}
                    placeholder={goalModalType === 'percent' ? '15' : '30000'}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl font-mono text-sm font-black focus:outline-none focus:border-indigo-600 focus:bg-white"
                  />
                  <span className="absolute right-3 top-2.5 text-xs font-black text-slate-400">
                    {goalModalType === 'percent' ? '%' : 'EUR'}
                  </span>
                </div>
              </div>

              {goalModalType === 'percent' && (
                <div>
                  <span className="block text-[9px] uppercase font-bold text-slate-400 mb-1.5">
                    {language === 'sk' ? 'Rýchla voľba:' : 'Quick presets:'}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {['8', '10', '12', '15', '20', '25', '30'].map(val => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setGoalModalValue(val)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold border transition-all ${
                          goalModalValue === val
                            ? 'bg-indigo-600 text-white border-indigo-700 shadow-2xs'
                            : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'
                        }`}
                      >
                        +{val}%
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-2xl text-[11px] text-slate-600 space-y-1">
                <div className="flex items-center justify-between text-indigo-950 font-bold">
                  <span>{language === 'sk' ? 'Prepočet pre vaše portfólio:' : 'Target for your portfolio:'}</span>
                  <span className="font-mono font-black text-xs text-indigo-700">
                    €{(goalModalType === 'fixed' 
                      ? (parseFloat(goalModalValue) || 0) 
                      : totalInvested * ((parseFloat(goalModalValue) || 15) / 100)
                    ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500">
                  {language === 'sk'
                    ? `Vychádza z investovaného kapitálu €${totalInvested.toLocaleString(undefined, { maximumFractionDigits: 0 })}.`
                    : `Based on €${totalInvested.toLocaleString(undefined, { maximumFractionDigits: 0 })} invested capital.`}
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowGoalModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 hover:text-slate-900 font-bold text-xs rounded-xl transition-all"
                >
                  {language === 'sk' ? 'Zrušiť' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={goalModalLoading}
                  className="px-5 py-2 bg-slate-900 hover:bg-black text-white font-extrabold text-xs rounded-xl shadow-sm transition-all disabled:opacity-50"
                >
                  {goalModalLoading ? '...' : (language === 'sk' ? 'Uložiť cieľ' : 'Save Goal')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
